import { z } from 'zod';
import { DeviceId, IsoTimestamp, UserId } from './common';

const DEVICE_PLATFORM_VALUES = ['macos', 'windows', 'linux', 'ios', 'android'] as const;
export const DevicePlatform = z.enum(DEVICE_PLATFORM_VALUES);
export type DevicePlatform = z.infer<typeof DevicePlatform>;

/** Forward-compat read variant — see #482. */
export const DevicePlatformReadable = z
  .enum([...DEVICE_PLATFORM_VALUES, 'unknown'] as const)
  .catch('unknown');
export type DevicePlatformReadable = z.infer<typeof DevicePlatformReadable>;

const DEVICE_KIND_VALUES = ['desktop', 'mobile'] as const;
export const DeviceKind = z.enum(DEVICE_KIND_VALUES);
export type DeviceKind = z.infer<typeof DeviceKind>;

/** Forward-compat read variant — see #482. */
export const DeviceKindReadable = z
  .enum([...DEVICE_KIND_VALUES, 'unknown'] as const)
  .catch('unknown');
export type DeviceKindReadable = z.infer<typeof DeviceKindReadable>;

const DEVICE_STATUS_VALUES = ['active', 'revoked'] as const;
export const DeviceStatus = z.enum(DEVICE_STATUS_VALUES);
export type DeviceStatus = z.infer<typeof DeviceStatus>;

/** Forward-compat read variant — see #482. */
export const DeviceStatusReadable = z
  .enum([...DEVICE_STATUS_VALUES, 'unknown'] as const)
  .catch('unknown');
export type DeviceStatusReadable = z.infer<typeof DeviceStatusReadable>;

/**
 * Per-user single-active-desktop invariant
 * ----------------------------------------
 * A paid user may pair multiple desktops (default cap 2 on Pro) but at most
 * ONE may be ACTIVE at a time — only the active device's worker is allowed to
 * run cycles. Standby devices observe via the tray flyout but don't burn LLM
 * budget. The active row is the single source of truth: there is exactly one
 * `isActive: true` row per user across all `DEVICE#*` SKs.
 *
 * `lastHeartbeatAt` is updated every ~60 s by the running worker so any other
 * device or the mobile UI can detect a stale active device and take over
 * without confirmation (see STALE_HEARTBEAT_MS).
 */
export const Device = z.object({
  userId: UserId,
  deviceId: DeviceId,
  kind: DeviceKindReadable,
  platform: DevicePlatformReadable,
  appVersion: z.string().optional(),
  pushToken: z.string().optional(),
  /** Human-friendly device name, e.g. "Adam's MacBook Pro". */
  name: z.string().optional(),
  // Defaulted to 'active' so existing rows (pre-revocation feature) read as
  // active without a backfill. Mobile's "revoke device" UI flips this to
  // 'revoked', which causes the next authenticate() call to 401.
  status: DeviceStatusReadable.default('active'),
  revokedAt: IsoTimestamp.optional(),
  lastSeenAt: IsoTimestamp,
  createdAt: IsoTimestamp,
  /** When the device was paired (= createdAt for desktops, kept for clarity). */
  pairedAt: IsoTimestamp.optional(),
  /** Whether this is the ACTIVE worker for the user. Exactly one row per user
   *  is true. Defaulted to false so legacy rows that don't carry the flag read
   *  as "standby" until a fresh activation. */
  isActive: z.boolean().default(false),
  /** Wall-clock the device was promoted to active (ms epoch). Reset on each
   *  activation so the mobile UI can show "Active since 12m". */
  activatedAt: z.number().int().optional(),
  /** Ms-epoch of the last worker heartbeat. Updated every 60 s while a cycle
   *  is dispatched; stale > STALE_HEARTBEAT_MS triggers takeover-without-
   *  confirmation. */
  lastHeartbeatAt: z.number().int().optional(),
});

export type Device = z.infer<typeof Device>;

export const RegisterDeviceRequest = z.object({
  kind: DeviceKind,
  platform: DevicePlatform,
  appVersion: z.string().optional(),
  pushToken: z.string().optional(),
  name: z.string().optional(),
});

/**
 * Heartbeat ping from the worker — fires every ~60 s while a cycle is
 * dispatched. The response tells the device whether it's still allowed to
 * run; if `isActive` flipped to false the worker stops cleanly at the next
 * safe checkpoint.
 */
export const HeartbeatRequest = z.object({
  deviceId: z.string(),
  runId: z.string().optional(),
  version: z.string().optional(),
});
export type HeartbeatRequest = z.infer<typeof HeartbeatRequest>;

export const HeartbeatResponse = z.object({
  isActive: z.boolean(),
  /** True when the active device is stale and any other device can take over
   *  without explicit confirmation. The mobile UI uses this to hide the
   *  "switch active worker" confirmation modal. */
  takeoverAvailable: z.boolean(),
  /** Server-side ms epoch echoed back so the desktop can detect clock skew. */
  serverTimeMs: z.number().int(),
});
export type HeartbeatResponse = z.infer<typeof HeartbeatResponse>;

/**
 * Active devices stale-takeover threshold. If `lastHeartbeatAt` is older than
 * this, any other device (or mobile) may activate itself without a
 * confirmation modal — the previously-active device's worker will see
 * `isActive: false` on its next heartbeat and stop.
 */
export const STALE_HEARTBEAT_MS = 10 * 60 * 1000;
